/**
 * A MASZK-SÁV (#76) — „X a Y Kft. nevében dolgozik".
 *
 * NEM ZÁRHATÓ BE, és nem görgethető el: amíg a sáv áll, minden, amit a
 * képernyőn teszel, az iroda nevében, a te neveddel kerül a naplóba. Egy
 * bezárható sáv után a maszkolt képernyő pontosan úgy nézne ki, mint az
 * ügyfélé — és pont ez a tévedés az, ami ellen a sáv van.
 *
 * Három dolgot tesz a megjelenésen túl:
 *   • a lap címe elé teszi a maszk jelét — egy fülsorban is látsszon;
 *   • fókuszkor és félpercenként újrakérdezi, ki van belépve — egy
 *     adminból visszavont munkamenet így magától véget ér, nem csak a
 *     következő kattintásnál;
 *   • a lejárat pillanatában ugyanezt teszi.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthProvider';
import { formatDateTime } from '@/i18n/generated';

/** Ilyen gyakran kérdezzük újra, él-e még a munkamenet. */
const RECHECK_MS = 30_000;

/**
 * A `setTimeout` legnagyobb késleltetése. Fölötte a böngésző AZONNAL futtat —
 * egy távoli lejárat így nem a lejáratkor, hanem rögtön kérdezne újra.
 */
const MAX_TIMEOUT_MS = 2_147_483_647;

export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { me, refresh, signOut, exitError } = useAuth();
  const actor = me?.actor.kind === 'impersonation' ? me.actor : null;
  // PRIMITÍV FÜGGŐSÉGEK: minden újrakérdezés új `me` objektumot ad, és egy
  // objektumra kötött effekt minden válasz után újraindítaná az időzítőket.
  const company = actor?.companyName ?? null;
  const expiresAt = actor?.expiresAt ?? null;

  useEffect(() => {
    if (company === null) {
      return;
    }
    const original = document.title;
    document.title = `${t('impersonation.titlePrefix', { company })} · ${original}`;
    return () => {
      document.title = original;
    };
  }, [company, t]);

  useEffect(() => {
    if (expiresAt === null) {
      return;
    }
    const recheck = () => void refresh();
    const untilExpiry = Math.min(
      MAX_TIMEOUT_MS,
      Math.max(0, new Date(expiresAt).getTime() - Date.now())
    );
    const interval = window.setInterval(recheck, RECHECK_MS);
    const expiry = window.setTimeout(recheck, untilExpiry);
    window.addEventListener('focus', recheck);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(expiry);
      window.removeEventListener('focus', recheck);
    };
  }, [expiresAt, refresh]);

  if (actor === null) {
    return null;
  }

  return (
    <section className="impersonation-bar" aria-label={t('impersonation.label')}>
      <p>
        <strong>{actor.name}</strong>
        {' · '}
        {t('impersonation.onBehalf', { company: actor.companyName })}
        {' · '}
        <span className="impersonation-bar__until">
          {t('impersonation.until', { time: formatDateTime(actor.expiresAt) })}
        </span>
      </p>
      <button type="button" onClick={signOut}>
        {t('impersonation.exit')}
      </button>
      {exitError ? (
        // A KILÉPÉS NEM SIKERÜLT — a munkamenet a szerveren nyitva van. Ezt
        // ki kell mondani: egy „lezárult" képernyő itt hazudna.
        <p className="impersonation-bar__error" role="alert">
          {t('impersonation.exitFailed')}
        </p>
      ) : null}
    </section>
  );
}
